import * as THREE from 'three'

import { createNormalizer } from '@/lib/utils'
import getMinimumBoundingBox from '@state/layout/minimalBoundingBox'
import {
  getZRotationFromQuaternionDeg,
  getZRotationFromQuaternionRad, globalToLocal, localToGlobal,
  type Point2D, type Point3D, type Pose2D,
  quaternionToRotationMatrix
} from '@utils/geometry.ts'

import type { LayoutFrame, TransformedLayout } from '@state/layout/types'
import type { StockLevel } from '@state/product'
import type {
  ShelfDTO,
  TransformedShelf,
  TransformedShelfWithoutLayers
} from '@state/shelf/types'

interface BoundingBox {
  minX: number
  minY: number
  maxX: number
  maxY: number
  width: number
  height: number
}

/**
 * Calculate the actual bounding box for transformed shelves, accounting for rotation.
 * This computes all four corners of each shelf and finds the min/max coordinates.
 */
function getShelvesBoundingBox(shelves: TransformedShelf[]): BoundingBox {
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity

  for (const shelf of shelves) {
    const rotationRad = shelf.rotationRad
    const cos = Math.cos(rotationRad)
    const sin = Math.sin(rotationRad)

    const corners = [
      { x: shelf.x, y: shelf.y },
      { x: shelf.x + shelf.width * cos, y: shelf.y + shelf.width * sin },
      { x: shelf.x - shelf.depth * sin, y: shelf.y + shelf.depth * cos },
      { x: shelf.x + shelf.width * cos - shelf.depth * sin, y: shelf.y + shelf.width * sin + shelf.depth * cos },
    ]

    for (const corner of corners) {
      minX = Math.min(minX, corner.x)
      minY = Math.min(minY, corner.y)
      maxX = Math.max(maxX, corner.x)
      maxY = Math.max(maxY, corner.y)
    }
  }

  return {
    minX,
    minY,
    maxX,
    maxY,
    width: maxX - minX,
    height: maxY - minY,
  }
}

interface OrientedBoundingBox {
  center: Point2D
  width: number
  height: number
  rotation: number
}

export function mirrorShelfOnXAxis(shelf: TransformedShelf, layoutHeight: number): TransformedShelf {
  const rotationRad = shelf.rotationRad
  const sin = Math.sin(rotationRad)
  const cos = Math.cos(rotationRad)

  return {
    ...shelf,
    x: shelf.x - shelf.depth * sin,
    y: layoutHeight - shelf.y - shelf.depth * cos,
    rotationDeg: -shelf.rotationDeg,
    rotationRad: -shelf.rotationRad,
    rotation: {
      ...shelf.rotation,
      x: -shelf.rotation.x,
      y: -shelf.rotation.y,
      z: -shelf.rotation.z,
    },
    center: {
      ...shelf.center,
      y: layoutHeight - shelf.center.y,
    },
  }
}

function getUnrotationAngle(obb: OrientedBoundingBox, mode: MODE): number {
  // The OBB rotation represents the angle of the u-axis (the primary edge direction)
  // We want to align to axis-aligned coordinates and ensure landscape mode for better screen utilization
  // If height > width, rotate an additional 90 degrees to make the layout landscape
  let angle = -obb.rotation

  if (mode === 'landscape' && obb.height > obb.width) {
    angle -= Math.PI / 2
  }

  if (mode === 'portrait' && obb.width > obb.height) {
    angle -= Math.PI / 2
  }

  return angle
}

function rotatePointAroundCenter(point: Point2D, center: Point2D, angle: number): Point2D {
  const cos = Math.cos(angle)
  const sin = Math.sin(angle)
  const dx = point.x - center.x
  const dy = point.y - center.y
  return {
    x: center.x + dx * cos - dy * sin,
    y: center.y + dx * sin + dy * cos,
  }
}

export function unrotateShelfByOBB(
  shelf: TransformedShelf,
  obb: OrientedBoundingBox | undefined,
  mode: MODE = 'landscape'
): TransformedShelf {
  if (!obb) return shelf

  return rotateShelf(shelf, obb.center, getUnrotationAngle(obb, mode))
}

function rotateShelf(shelf: TransformedShelf, pivot: Point2D, angle: number): TransformedShelf {
  const newCenter = rotatePointAroundCenter(shelf.center, pivot, angle)
  const newPosition = rotatePointAroundCenter({ x: shelf.x, y: shelf.y }, pivot, angle)

  const newRotationRad = shelf.rotationRad + angle
  const newRotationDeg = newRotationRad * (180 / Math.PI)

  const newRotationQuaternion = new THREE.Quaternion()
    .setFromAxisAngle(new THREE.Vector3(0, 0, 1), newRotationRad)

  return {
    ...shelf,
    x: newPosition.x,
    y: newPosition.y,
    rotationRad: newRotationRad,
    rotationDeg: newRotationDeg,
    rotation: {
      x: newRotationQuaternion.x,
      y: newRotationQuaternion.y,
      z: newRotationQuaternion.z,
      w: newRotationQuaternion.w,
    },
    center: {
      ...shelf.center,
      x: newCenter.x,
      y: newCenter.y,
    },
  }
}

export function toTransformedShelf(shelf: ShelfDTO): TransformedShelfWithoutLayers {
  return {
    ...shelf,
    rotationRad: getZRotationFromQuaternionRad(shelf.rotation),
    rotationDeg: getZRotationFromQuaternionDeg(shelf.rotation),
    center: calculateShelfCenter(shelf),
    layers: [],
  }
}

function alignShelf(shelf: TransformedShelf, minX: number, minY: number): TransformedShelfWithoutLayers {
  return {
    ...shelf,
    x: shelf.x - minX,
    y: shelf.y - minY,
    center: {
      ...shelf.center,
      x: shelf.center.x - minX,
      y: shelf.center.y - minY,
    },
    layers: [],
  }
}

type MODE = 'landscape' | 'portrait'

/**
 * Mirrors a shelf set the way {@link recalculateShelves} does and returns its oriented bounding
 * box, i.e. the box whose angle decides how the whole layout is rotated for display.
 */
function getLayoutBoundingBox(shelves: ShelfDTO[]): OrientedBoundingBox | undefined {
  const transformedShelves = shelves.map(toTransformedShelf)

  const { minY, maxY } = transformedShelves.reduce((acc, shelf) => {
    return { minY: Math.min(acc.minY, shelf.y), maxY: Math.max(acc.maxY, shelf.y) }
  }, { minY: Infinity, maxY: -Infinity })

  return getMinimumBoundingBox(transformedShelves.map(shelf => mirrorShelfOnXAxis(shelf, maxY - minY)))
}

/**
 * An oriented bounding box's angle is only defined modulo 180°: opposite edges of the same minimal
 * box are equally valid u-axes. Two different shelf sets for the same store — the deploy-db map and
 * the scan map — can therefore resolve to opposite half-turns and render upside down relative to
 * each other.
 *
 * Taking the angle (and the landscape/portrait extents that depend on it) from a fixed reference set
 * makes the rendered orientation independent of which set is being displayed. The pivot stays local,
 * so shelves are still rotated about their own centre.
 */
function withOrientationOf(
  local: OrientedBoundingBox | undefined,
  reference: OrientedBoundingBox | undefined
): OrientedBoundingBox | undefined {
  if (!local || !reference) return local

  return { ...local, rotation: reference.rotation, width: reference.width, height: reference.height }
}

/**
 * @param orientationReference shelves whose orientation the result should adopt. Defaults to
 *   `shelves`, which reproduces the standalone behaviour. Pass the scan's shelves when rendering a
 *   deploy-db layout so both origins agree.
 */
export function recalculateShelves(
  shelves: ShelfDTO[],
  mode: MODE = 'landscape',
  orientationReference?: ShelfDTO[]
): TransformedLayout {
  const transformedShelves = shelves.map(toTransformedShelf)

  const { minY, maxY } = transformedShelves.reduce((acc, shelf) => {
    return { minY: Math.min(acc.minY, shelf.y), maxY: Math.max(acc.maxY, shelf.y) }
  }, { minY: Infinity, maxY: -Infinity })

  const mirrorHeight = maxY - minY
  const mirroredShelves = transformedShelves.map(shelf => mirrorShelfOnXAxis(shelf, mirrorHeight))
  const localObb = getMinimumBoundingBox(mirroredShelves)
  const obb = orientationReference
    ? withOrientationOf(localObb, getLayoutBoundingBox(orientationReference))
    : localObb
  const unrotatedShelves = mirroredShelves.map(shelf => unrotateShelfByOBB(shelf, obb, mode))
  const finalObb = getShelvesBoundingBox(unrotatedShelves)
  const alignedShelves = unrotatedShelves.map(shelf => alignShelf(shelf, finalObb.minX, finalObb.minY))
  const equippedShelves = alignedShelves.map(shelf => {
    const originalShelf = shelves.find(s => s.id === shelf.id)!
    return equipShelfWithLayers(shelf, originalShelf)
  })

  return {
    width: finalObb.width,
    height: finalObb.height,
    shelves: equippedShelves,
    frame: {
      mirrorHeight,
      pivot: obb?.center ?? { x: 0, y: 0 },
      angle: obb ? getUnrotationAngle(obb, mode) : 0,
      offset: { x: finalObb.minX, y: finalObb.minY },
    },
  }
}

/**
 * Draws shelves on the floor of another layout of the same store, instead of fitting a floor around
 * them. All scans of a store share one coordinate system, so part of the store lands where it is in
 * the whole store: its scale, angle and position don't depend on which shelves it happens to contain.
 */
export function recalculateShelvesInFrame(
  shelves: ShelfDTO[],
  { width, height, frame }: Pick<TransformedLayout, 'width' | 'height' | 'frame'>
): TransformedLayout {
  return {
    width,
    height,
    frame,
    shelves: shelves.map(dto => {
      const mirrored = mirrorShelfOnXAxis(toTransformedShelf(dto), frame.mirrorHeight)
      const rotated = rotateShelf(mirrored, frame.pivot, frame.angle)
      return equipShelfWithLayers(alignShelf(rotated, frame.offset.x, frame.offset.y), dto)
    }),
  }
}

/**
 * Moves a pose from raw map coordinates into the layout coordinates of the shelves it was
 * recalculated with, e.g. a robot-recorded panorama position.
 */
export function toLayoutPose(pose: Pose2D, frame: LayoutFrame): Pose2D {
  const mirrored = { x: pose.x, y: frame.mirrorHeight - pose.y }
  const rotated = rotatePointAroundCenter(mirrored, frame.pivot, frame.angle)

  return {
    x: rotated.x - frame.offset.x,
    y: rotated.y - frame.offset.y,
    rotationRad: -pose.rotationRad + frame.angle,
  }
}

/**
 * Adds the price labels, which only come with a single shelf, not with the layout (about 10,000 per scan).
 * Placed in the frame of the scan's own shelf: on a deploy-db map the backend scales facings to the
 * deploy-db shelf (FacingRemapper), which keeps their fractions of the width, so labels and facings still line up.
 */
export function withPriceLabels<T extends Pick<TransformedShelf, 'layers'>>(shelf: T, scanShelf: ShelfDTO): T {
  const transformationMatrix = quaternionToRotationMatrix(scanShelf.rotation)
  const position: Point3D = { x: scanShelf.x, y: scanShelf.y, z: 0 }
  const widthNormalizer = createNormalizer(0, scanShelf.width)
  const labelsByLayer = new Map(scanShelf.layers.map(layer => [layer.id, layer.priceLabels]))

  return {
    ...shelf,
    layers: shelf.layers.map(layer => ({
      ...layer,
      priceLabels: (labelsByLayer.get(layer.id) ?? []).map(label => ({
        id: label.id,
        left: widthNormalizer(globalToLocal(label.position, position, transformationMatrix).x),
        gtin: label.gtin,
      })),
    })),
  }
}

function equipShelfWithLayers(shelf: TransformedShelfWithoutLayers, originalShelf: ShelfDTO): TransformedShelf {
  const layers = originalShelf.layers

  // Use the original shelf's rotation because the facing coordinates (leftCorner, rightCorner)
  // are still in the original global coordinate space, not the transformed space
  const originalRotation = originalShelf.rotation

  const transformationMatrix = quaternionToRotationMatrix(originalRotation)
  const originalPosition: Point3D = { x: originalShelf.x, y: originalShelf.y, z: 0 }

  const layerWidthNormalizer = createNormalizer(0, shelf.width)
  const layerHeightNormalizer = createNormalizer(0, shelf.height)

  const transformedLayers = layers.map(layer => {
    return {
      id: layer.id,
      y: layerHeightNormalizer(layer.height),
      height: 0,
      facings: layer.facings.map(facing => {
        const localLeft: Point3D = globalToLocal(facing.leftCorner, originalPosition, transformationMatrix)
        const localRight: Point3D = globalToLocal(facing.rightCorner, originalPosition, transformationMatrix)

        const leftNormalized = layerWidthNormalizer(localLeft.x)
        const rightNormalized = layerWidthNormalizer(localRight.x)
        const width = rightNormalized - leftNormalized

        return {
          id: facing.id,
          left: leftNormalized,
          width,
          gtin: facing.gtin,
          stockLevel: facing.stockLevel as StockLevel | undefined,
        }
      }),
      priceLabels: [],
    }
  })

  transformedLayers.sort((a, b) => a.y - b.y)

  transformedLayers.forEach((layer, index) => {
    const nextLayer = transformedLayers[index + 1]
    const nextLayerHeight = nextLayer?.y ?? 1
    layer.height = nextLayerHeight - layer.y
  })

  return {
    ...shelf,
    layers: transformedLayers,
  }
}

function calculateShelfCenter(shelf: ShelfDTO): Point3D {
  const rotationMatrix = quaternionToRotationMatrix(shelf.rotation)

  const localCenter: Point3D = {
    x: shelf.width / 2,
    y: shelf.depth / 2,
    z: 0,
  }

  return localToGlobal(localCenter, { x: shelf.x, y: shelf.y, z: 0 }, rotationMatrix)
}